<template>
  <div v-if="show" :id="interactionId" :class="windowClass" :style="CSS_VARS">
    <div :class="classesList">
      <div :class="ecsWindowClass" @click="focus">
        <div class="header surface lighten-1 ecs-window-header">
          <div v-if="interactionWindow.isMinimized" class="minimized-title" :class="{ blink: shouldBlink }">
            <v-avatar
              v-show="pendingNotifications"
              class="ecs-minimized-window-notification-badget"
              :size="16"
              color="primary"
            >
              <span class="white--text">{{ pendingNotifications }}</span>
            </v-avatar>
            <span class="ecs-minimized-window-title">{{ title }}</span>
          </div>

          <div class="buttons" :class="{ blink: hasBlinkClass }">
            <v-icon v-if="!interactionWindow.isMinimized" small @click.prevent.stop="handleMinimizeWindow">
              mdi-window-minimize
            </v-icon>
            <v-icon v-if="!interactionWindow.isRestored" small @click="handleRestoreWindow">
              mdi-window-restore
            </v-icon>
            <v-icon v-if="interactionWindow.isRestored" small @click.prevent.stop="handleMaximizeWindow">
              mdi-window-maximize
            </v-icon>
            <v-icon small @click.prevent.stop="handleWindowClose">
              mdi-window-close
            </v-icon>
          </div>
        </div>
        <!-- Custom Content Section -->
        <div v-if="!interactionWindow.isMinimized" class="content">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import debounce from 'lodash/debounce';

export default {
  name: 'EcsWindow',

  props: {
    interactionId: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    minimizedHeight: {
      type: Number,
      default: 24
    },
    minimizedWidth: {
      type: Number,
      default: 240
    },
    restoredHeight: {
      type: Number,
      default: 584
    },
    restoredWidth: {
      type: Number,
      default: 480
    },
    itemClass: {
      type: String,
      default: ''
    },
    itemContentClass: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      shouldBlink: false
    };
  },

  computed: {
    ...mapGetters('NOTIFICATIONS', ['getNumberOfNotificationsForInteraction']),

    ...mapGetters('WINDOWS', ['getInteractionWindow', 'focusedInteractionId']),

    interactionWindow() {
      return this.getInteractionWindow(this.interactionId);
    },

    pendingNotifications() {
      return this.getNumberOfNotificationsForInteraction(this.interactionId);
    },

    isFocused() {
      return this.focusedInteractionId === this.interactionId;
    },

    CSS_VARS() {
      return {
        '--minimized-height': `${this.minimizedHeight}px`,
        '--minimized-width': `${this.minimizedWidth}px`,
        '--restored-height': `${this.restoredHeight}px`,
        '--restored-width': `${this.restoredWidth}px`
      };
    },
    windowClass() {
      return {
        [this.itemClass]: true,
        'ml-3': true,
        'mt-3': !this.interactionWindow.isMinimized,
        [this.interactionWindow.currentState.toLowerCase()]: true
      };
    },
    ecsWindowClass() {
      return {
        'ecs-window': true,
        minimized: this.interactionWindow.isMinimized,
        'elevation-3': true,
        focus: this.isFocused
      };
    },
    show() {
      return this.interactionWindow && !this.interactionWindow.isClosed;
    },
    hasBlinkClass() {
      return this.shouldBlink && this.interactionWindow.isMinimized;
    },
    classesList() {
      return [this.itemContentClass, this.interactionWindow.currentState.toLowerCase()];
    }
  },

  watch: {
    interactionWindow(current, previous) {
      if (previous.currentState === current.currentState && current.isClosed) {
        return;
      }

      if (previous.currentState !== current.currentState) {
        this.$emit(current.currentState.toLowerCase(), this.interactionId);
      }

      this.$emit('window-state-changed');
    },
    pendingNotifications(value) {
      if (value > 0) {
        this.shouldBlink = true;
        this.setBlinkingTimeout();
      }
    }
  },

  mounted() {
    this.$emit('window-mounted', this.interactionId);
  },

  methods: {
    ...mapActions('WINDOWS', [
      'restoreInteractionWindow',
      'minimizeInteractionWindow',
      'setFocusedInteractionId',
      'closeInteractionWindow',
      'removeFocusedInteractionId',
      'maximizeInteractionWindow'
    ]),

    focus() {
      this.setFocusedInteractionId(this.interactionId);
    },

    blur() {
      this.removeFocusedInteractionId(this.interactionId);
    },

    handleRestoreWindow() {
      if (this.interactionWindow.isMinimized) {
        this.$el.classList.add('from-minimize');
      }
      this.restoreInteractionWindow(this.interactionId);
      this.focus();
      this.$emit('restored-window', this.interactionId);
    },

    setBlinkingTimeout: debounce(function() {
      this.shouldBlink = false;
    }, 2000),

    handleMinimizeWindow() {
      this.minimizeInteractionWindow(this.interactionId);
      this.$emit('minimized-window', this.interactionId);
      this.blur();
    },

    handleMaximizeWindow() {
      this.maximizeInteractionWindow(this.interactionId);
      this.$emit('maximized-window', this.interactionId);
    },

    handleWindowClose() {
      this.closeInteractionWindow(this.interactionId);
      this.$emit('closed-window', this.interactionId);
      this.blur();
    }
  }
};
</script>

<style src="./scoped.css" scoped />
